import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useNotification } from '@/components/notifications';

interface UsePendingChangesProps<T> {
  onSave: (changes: Record<string, T>) => Promise<void>;
  successMessage?: string;
}

export const usePendingChanges = <T>({
  onSave,
  successMessage,
}: UsePendingChangesProps<T>) => {
  const notification = useNotification();
  const [pendingChanges, setPendingChanges] = useState<Record<string, T>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = useCallback(
    (key: string, newValue: T, originalValue: T) => {
      setPendingChanges((prev) => {
        const newChanges = { ...prev };
        if (newValue === originalValue) {
          delete newChanges[key];
        } else {
          newChanges[key] = newValue;
        }
        return newChanges;
      });
    },
    [],
  );

  const updatePendingChanges = useCallback(
    (updater: (prev: Record<string, T>) => Record<string, T>) => {
      setPendingChanges(updater);
    },
    [],
  );

  const getCurrentValue = useCallback(
    (key: string, originalValue: T): T => {
      if (key in pendingChanges) {
        return pendingChanges[key];
      }
      return originalValue;
    },
    [pendingChanges],
  );

  const save = useCallback(async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    setIsSaving(true);
    try {
      await onSave(pendingChanges);
      setPendingChanges({});
      setShowConfirmModal(false);
      if (successMessage) {
        notification.success(successMessage);
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'pending-changes-save' },
      });
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save changes';
      notification.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [pendingChanges, onSave, notification, successMessage]);

  const discard = useCallback(() => {
    setPendingChanges({});
    setShowConfirmModal(false);
  }, []);

  return {
    pendingChanges,
    setPendingChanges: updatePendingChanges,
    hasPendingChanges: Object.keys(pendingChanges).length > 0,
    showConfirmModal,
    setShowConfirmModal,
    isSaving,
    handleToggle,
    getCurrentValue,
    save,
    discard,
  };
};
